import { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2, Minus, Plus, RotateCcw } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const MOBILE_QUERY = '(max-width: 640px)';
const MIN_ZOOM = 0.75;
const MAX_ZOOM = 1.5;
const ZOOM_STEP = 0.15;

const getPdfErrorMessage = (error) => error?.message || String(error || 'Unknown PDF.js error');

const classifyPdfError = (error, fallback) => {
  const message = getPdfErrorMessage(error);
  const lowerMessage = message.toLowerCase();
  if (lowerMessage.includes('worker')) {
    return `PDF.js worker failed: ${message}`;
  }
  return `${fallback}: ${message}`;
};

const PdfStatus = ({ icon, title, description, actionLabel, onAction }) => (
  <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-3 rounded-md border border-slate-200 bg-white px-6 text-center text-sm text-muted-foreground shadow-sm">
    {icon}
    <div>
      <p className="font-medium text-foreground">{title}</p>
      {description ? <p className="mt-1 text-xs sm:text-sm">{description}</p> : null}
    </div>
    {actionLabel && onAction ? (
      <Button type="button" variant="outline" onClick={onAction}>
        {actionLabel}
      </Button>
    ) : null}
  </div>
);

export const PdfPreviewDialog = ({
  open,
  onOpenChange,
  title,
  description,
  actions,
  pdfData,
  sourceKey,
  loading,
  loadingTitle = 'Loading PDF preview',
  loadingDescription,
  errorMessage,
  unavailableMessage = 'PDF preview unavailable.',
  fallbackActionLabel,
  onFallbackAction,
}) => {
  const viewportRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [renderError, setRenderError] = useState('');
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsNarrow(mediaQuery.matches);
    update();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', update);
      return () => mediaQuery.removeEventListener('change', update);
    }

    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width || 0;
      setContainerWidth(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setNumPages(null);
    setPageNumber(1);
    setZoom(1);
    setRenderError('');
  }, [open, sourceKey]);

  const documentFile = useMemo(() => {
    if (!pdfData) return null;
    return { data: pdfData };
  }, [pdfData]);

  const pageWidth = useMemo(() => {
    const width = containerWidth || (isNarrow ? 360 : 860);
    const fittedWidth = Math.max(260, Math.min(width - 16, isNarrow ? 620 : 860));
    return Math.round(fittedWidth * zoom);
  }, [containerWidth, isNarrow, zoom]);

  const visiblePages = useMemo(() => {
    if (!numPages) return [];
    if (isNarrow) return [Math.min(pageNumber, numPages)];
    return Array.from({ length: numPages }, (_, index) => index + 1);
  }, [isNarrow, numPages, pageNumber]);

  const hasError = Boolean(errorMessage || renderError);
  const canNavigate = isNarrow && Number(numPages) > 1;
  const zoomPercent = Math.round(zoom * 100);

  const controls = (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-white px-3 py-2 text-sm">
      <div className="flex min-h-10 items-center gap-2">
        {canNavigate ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 min-w-10 px-3"
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber((page) => Math.max(1, page - 1))}
              aria-label="Previous PDF page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-24 text-center text-xs font-medium text-slate-700">
              Page {Math.min(pageNumber, numPages)} of {numPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 min-w-10 px-3"
              disabled={pageNumber >= numPages}
              onClick={() => setPageNumber((page) => Math.min(numPages, page + 1))}
              aria-label="Next PDF page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <span className="text-xs font-medium text-slate-700">
            {numPages ? `${numPages} ${numPages === 1 ? 'page' : 'pages'}` : 'Preparing pages'}
          </span>
        )}
      </div>

      <div className="flex min-h-10 items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-10 min-w-10 px-3"
          disabled={zoom <= MIN_ZOOM}
          onClick={() => setZoom((value) => Math.max(MIN_ZOOM, Number((value - ZOOM_STEP).toFixed(2))))}
          aria-label="Zoom out"
        >
          <Minus className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-10 min-w-16 px-3"
          disabled={zoom === 1}
          onClick={() => setZoom(1)}
          aria-label="Reset zoom"
        >
          <RotateCcw className="h-4 w-4" />
          <span className="text-xs">{zoomPercent}%</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-10 min-w-10 px-3"
          disabled={zoom >= MAX_ZOOM}
          onClick={() => setZoom((value) => Math.min(MAX_ZOOM, Number((value + ZOOM_STEP).toFixed(2))))}
          aria-label="Zoom in"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] w-[calc(100vw-1rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:h-[90vh]">
        <DialogHeader className="border-b px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <DialogTitle className="text-base sm:text-lg">{title}</DialogTitle>
              {description ? (
                <DialogDescription asChild>
                  <div className="mt-1 text-xs sm:text-sm">{description}</div>
                </DialogDescription>
              ) : null}
            </div>
            {actions ? <div className="flex flex-col gap-2 sm:flex-row sm:items-center">{actions}</div> : null}
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col bg-slate-100 p-2 sm:p-4">
          {loading ? (
            <PdfStatus
              icon={<Loader2 className="h-6 w-6 animate-spin text-royal-600" />}
              title={loadingTitle}
              description={loadingDescription}
            />
          ) : hasError ? (
            <PdfStatus
              icon={<AlertTriangle className="h-6 w-6 text-amber-600" />}
              title={errorMessage || renderError}
              description="Try again, or use the receipt action below if one is available."
              actionLabel={fallbackActionLabel}
              onAction={onFallbackAction}
            />
          ) : documentFile ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
              {controls}
              <div ref={viewportRef} className="min-h-0 flex-1 overflow-auto bg-slate-100 px-2 py-3 sm:px-4">
                <Document
                  key={sourceKey || `pdf-${pdfData?.byteLength || 0}`}
                  file={documentFile}
                  loading={
                    <PdfStatus
                      icon={<Loader2 className="h-6 w-6 animate-spin text-royal-600" />}
                      title="Rendering PDF"
                      description="Preparing the receipt pages."
                    />
                  }
                  error=""
                  onSourceError={(error) => {
                    console.error('[pdf-preview] PDF source error', error);
                    setRenderError(classifyPdfError(error, 'PDF source failed'));
                  }}
                  onLoadSuccess={({ numPages: loadedPages }) => {
                    setNumPages(loadedPages);
                    setPageNumber(1);
                    setRenderError('');
                  }}
                  onLoadError={(error) => {
                    console.error('[pdf-preview] PDF load/parse error', error);
                    setRenderError(classifyPdfError(error, 'PDF parse/render failed'));
                  }}
                >
                  <div className="mx-auto flex w-max max-w-none flex-col items-center gap-4">
                    {visiblePages.map((page) => (
                      <Page
                        key={`${sourceKey || 'pdf'}-${page}-${pageWidth}`}
                        pageNumber={page}
                        width={pageWidth}
                        renderAnnotationLayer
                        renderTextLayer
                        onRenderError={(error) => {
                          console.error('[pdf-preview] PDF page render error', { page, error });
                          setRenderError(classifyPdfError(error, `PDF page ${page} render failed`));
                        }}
                        loading={
                          <div className="flex h-80 w-72 items-center justify-center rounded-md border bg-white text-sm text-muted-foreground">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading page {page}
                          </div>
                        }
                      />
                    ))}
                  </div>
                </Document>
              </div>
            </div>
          ) : (
            <PdfStatus
              icon={<AlertTriangle className="h-6 w-6 text-amber-600" />}
              title={unavailableMessage}
              actionLabel={fallbackActionLabel}
              onAction={onFallbackAction}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
